import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  type TaxRefundMethodVersion,
  type TaxRefundType,
} from '@/features/tax-profile/tax-refund-methods/TaxRefundMethod'
import { task } from 'mobx-task'
import { action, computed, observable } from 'mobx'
import type {
  TaxRefundDeliveryMethod,
  TaxRefundDeliveryMethodType,
} from '@/features/tax-profile/tax-refund-methods/TaxRefundDeliveryMethod'
import { CheckDeliveryMethodEditorViewModel } from '@/features/tax-profile/tax-refund-methods/CheckDeliveryMethodEditorViewModel'
import { DirectDepositDeliveryMethodEditorViewModel } from '@/features/tax-profile/tax-refund-methods/DirectDepositDeliveryMethodEditorViewModel'
import { FutureTaxCreditDeliveryMethodEditorViewModel } from './FutureTaxCreditDeliveryMethodEditorViewModel'
import { required, validationContext } from 'validx'
import { validateObject, when } from '@/utils/validx-util'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { TaxRefundMethodStore } from './TaxRefundMethodStore'
import type { SnackbarErrorHandlerFactory } from '@/features/shell'
import { match } from '@/utils/pattern-match-util'
import { filter } from 'rxjs'

/**
 * Editor view model for tax refund methods.
 */
export class TaxRefundMethodEditorViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel()

  readonly checkVm = new CheckDeliveryMethodEditorViewModel()
  readonly directDepositVm = new DirectDepositDeliveryMethodEditorViewModel()
  readonly futureTaxCreditVm =
    new FutureTaxCreditDeliveryMethodEditorViewModel()

  /**
   * Observable that emits when a tax refund method is delisted.
   */
  readonly taxRefundMethodDelisted$

  /**
   * Observable that emits when a tax refund method is added.
   */
  readonly taxRefundMethodAdded$

  /**
   * The selected type, used to determine which sub-editor to delegate to.
   */
  @observable accessor methodType: TaxRefundDeliveryMethodType | null = null

  /**
   * Whether this is a new method (no existing method passed in).
   */
  @observable accessor isNew = false

  /**
   * The method being edited, if any.
   */
  @observable accessor method: TaxRefundMethodVersion | null = null

  readonly validation = validationContext(this, {
    methodType: [required('Please select a delivery method')],
    checkVm: [when(() => this.methodType === 'Check', validateObject())],
    directDepositVm: [
      when(() => this.methodType === 'DirectDeposit', validateObject()),
    ],
    futureTaxCreditVm: [
      when(() => this.methodType === 'FutureTaxCredit', validateObject()),
    ],
  })

  /**
   * The refund type for this editor.
   */
  readonly refundType: TaxRefundType

  constructor(
    private readonly _taxRefundMethodStore: TaxRefundMethodStore,
    private readonly _createSnackbarErrorHandler: SnackbarErrorHandlerFactory,
    private readonly _options: TaxRefundMethodEditorViewModelOptions,
  ) {
    this.refundType = _options.refundType
    this.taxRefundMethodDelisted$ =
      this._taxRefundMethodStore.taxRefundMethodDelisted$
    this.taxRefundMethodAdded$ =
      this._taxRefundMethodStore.taxRefundMethodVersions.additions$.pipe(
        filter((m) => m.refundType === this.refundType),
      )
  }

  /**
   * Whether the editor is read-only.
   */
  @computed get isReadOnly() {
    return this._options.isReadOnly?.() ?? false
  }

  /**
   * Available delivery method types based on the refund type.
   */
  @computed
  get availableDeliveryMethodTypes(): readonly TaxRefundDeliveryMethodType[] {
    if (this.refundType === 'Business') {
      return ['DirectDeposit', 'Check', 'FutureTaxCredit']
    }

    return ['DirectDeposit', 'Check']
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Resets the view model to its initial state based on the provided method.
   * @param method
   */
  @action.bound
  reset(method?: TaxRefundMethodVersion | null) {
    this.directDepositVm.reset()
    this.checkVm.reset()
    this.futureTaxCreditVm.reset()
    this.methodType = null
    this.isNew = !method
    this.method = method ?? null

    switch (method?.deliveryMethod.type) {
      case 'DirectDeposit': {
        this.methodType = 'DirectDeposit'
        this.directDepositVm.reset(method.deliveryMethod)
        break
      }
      case 'Check': {
        this.methodType = 'Check'
        this.checkVm.reset(method.deliveryMethod)
        break
      }
      case 'FutureTaxCredit': {
        this.methodType = 'FutureTaxCredit'
        this.futureTaxCreditVm.reset(method.deliveryMethod)
        break
      }
    }
  }

  /**
   * Shows the dialog.
   */
  @action.bound
  showDialog(method?: TaxRefundMethodVersion | null) {
    this.reset(method)
    this.modal.show()
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.modal.close()
  }

  /**
   * Saves the tax refund method.
   */
  save = task.resolved(async () => {
    if (this.isReadOnly) {
      return
    }

    this.validation.reset().validate()
    if (!this.validation.isValid) {
      return
    }

    if (!this.methodType) {
      return
    }

    const deliveryMethod = match(this.methodType, {
      DirectDeposit: () => this.directDepositVm.toDeliveryMethod(),
      Check: () => this.checkVm.toDeliveryMethod(),
      FutureTaxCredit: () => this.futureTaxCreditVm.toDeliveryMethod(),
    })

    await this._save(deliveryMethod)
      .then(this._options.onSaved)
      .then(() => this.close())
      .catch(
        this._createSnackbarErrorHandler('Failed to save tax refund method'),
      )
  })

  private async _save(deliveryMethod: TaxRefundDeliveryMethod) {
    if (!this.method) {
      return await this._taxRefundMethodStore.createTaxRefundMethod({
        refundType: this.refundType,
        deliveryMethod,
      })
    }

    return await this._taxRefundMethodStore.updateTaxRefundMethod({
      id: this.method.id,
      deliveryMethod,
    })
  }
}

export interface TaxRefundMethodEditorViewModelOptions {
  /**
   * The refund type for this editor.
   */
  refundType: TaxRefundType
  /**
   * Whether the editor is read-only.
   */
  isReadOnly?: () => boolean
  /**
   * Called when the method has been saved.
   *
   * @param method
   */
  onSaved(method: TaxRefundMethodVersion): Promise<unknown>
}
